import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { TranslationKey } from '@/types/translations';
import en from '@/locales/en.json';
import fr from '@/locales/fr.json';
import it from '@/locales/it.json';

export type Language = 'fr' | 'en' | 'it';

type Translations = typeof en;

type LocalizedCopy = { fr: string; en: string; it: string };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  /** Inline FR / EN / IT copy for places not yet moved into JSON */
  tl: (fr: string, en: string, it: string) => string;
  languages: { code: Language; label: string; flag: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGES: LanguageContextType['languages'] = [
  { code: 'fr', label: 'Français', flag: '/flags/icons8-france-48.png' },
  { code: 'it', label: 'Italiano', flag: '/flags/icons8-italy-48.png' },
  { code: 'en', label: 'English', flag: '/flags/icons8-english-48.png' },
];

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('fr');

  const translations: Record<Language, Translations> = {
    fr,
    it,
    en,
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage === 'fr' || savedLanguage === 'en' || savedLanguage === 'it') {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: TranslationKey): string => {
    const keys = key.split('.');
    let value: unknown = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return key;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  const tl = (frText: string, enText: string, itText: string): string => {
    if (language === 'it') return itText;
    if (language === 'en') return enText;
    return frText;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, tl, languages: LANGUAGES }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export type { LocalizedCopy };
